import {
  getStudyMilestoneTreeNodeId,
  formatStudyPlaceholderTreeLabel,
  isAdHocStudyPlaceholder,
  isPendingStudyPlaceholder,
  resolvePlaceholderSignProgress,
  studyPlaceholderCanUpload,
  studyPlaceholderRequiresSignature,
  stripGuid,
} from "./isfPlaceholderService";

function normalizeStaffId(id) {
  return String(id || "").trim().toLowerCase();
}

function isOverduePlaceholder(row) {
  if (!row) return false;
  if (String(row.Status || "").toLowerCase() === "overdue") return true;
  const due = row.DueDate || row.dueDate;
  if (!due) return false;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return false;
  return d < new Date(new Date().toDateString());
}

function placeholderToTreeNode(row) {
  const milestoneParentId = getStudyMilestoneTreeNodeId(row);
  return {
    id: `ph-${row.StudyPlaceholderId}`,
    name: row.PlaceholderName,
    type: "placeholder",
    parentId: milestoneParentId || stripGuid(row.FolderIdRef),
    studyPlaceholderId: row.StudyPlaceholderId,
    StudyPlaceholderId: row.StudyPlaceholderId,
    folderName: row.FolderName,
    FolderName: row.FolderName,
    FolderIdRef: row.FolderIdRef,
    PlaceholderName: row.PlaceholderName,
    MilestoneCode: row.MilestoneCode,
    Status: row.Status,
    DueDate: row.DueDate,
  };
}

function documentToTreeNode(doc, placeholder) {
  const milestoneParentId = placeholder
    ? getStudyMilestoneTreeNodeId(placeholder)
    : null;
  return {
    id: doc.DocumentIdRef,
    name: doc.FileName,
    type: "file",
    parentId: milestoneParentId || stripGuid(doc.FolderIdRef),
    DocumentIdRef: doc.DocumentIdRef,
    VersionNo: doc.VersionNo,
    FileName: doc.FileName,
    IsMandatorySign: doc.IsMandatorySign,
    isMandatorySign:
      doc.IsMandatorySign === 1 ||
      doc.IsMandatorySign === true ||
      (placeholder != null && studyPlaceholderRequiresSignature(placeholder)),
    studyPlaceholderId: placeholder?.StudyPlaceholderId ?? null,
  };
}

function hasPendingSignature(placeholder, staffList, roleOptions, documents) {
  if (!studyPlaceholderRequiresSignature(placeholder)) return false;
  if (isPendingStudyPlaceholder(placeholder, documents)) return false;
  const sign = resolvePlaceholderSignProgress(
    placeholder,
    staffList,
    roleOptions,
    documents,
  );
  if (!sign.document) return false;
  if (sign.signComplete) return false;
  if (sign.totalSigners > 0) return sign.signedCount < sign.totalSigners;
  const doc = sign.document;
  return doc.IsMandatorySign === 1 || doc.IsMandatorySign === true;
}

function isUserEligibleToSignPlaceholder(
  placeholder,
  documents,
  userData,
  staffList,
  roleOptions,
) {
  if (!studyPlaceholderRequiresSignature(placeholder)) return false;
  const sign = resolvePlaceholderSignProgress(
    placeholder,
    staffList,
    roleOptions,
    documents,
  );
  if (!sign.document) return false;
  const myId = normalizeStaffId(userData?.StaffId);
  if (!myId) return false;

  let signerIds = sign.mandatorySignerIds.map((id) => normalizeStaffId(id)).filter(Boolean);
  if (!signerIds.length) {
    signerIds = (sign.mandatorySignUsers || [])
      .map((user) => normalizeStaffId(user.value))
      .filter(Boolean);
  }
  if (!signerIds.length) return false;
  if (!signerIds.includes(myId)) return false;
  return !sign.signedIds.some((id) => normalizeStaffId(id) === myId);
}

function buildTaskSubtitle(row) {
  const parts = [];
  if (row.FolderName || row.folderName) {
    parts.push(row.FolderName || row.folderName);
  }
  if (row.MilestoneCode) {
    parts.push(row.MilestoneCode);
  }
  return parts.join(" · ") || "Expected document";
}

export function buildSiteOverviewSnapshot({
  placeholders = [],
  documents = [],
  userData = null,
  staffList = [],
  roleOptions = [],
} = {}) {
  const rows = (placeholders || []).filter(
    (p) => String(p.Status || "").toLowerCase() !== "waived",
  );

  let uploadDone = 0;
  let uploadTotal = rows.length;
  let signDone = 0;
  let signTotal = 0;
  let overdueCount = 0;
  const tasks = [];

  rows.forEach((row) => {
    const pendingUpload = isPendingStudyPlaceholder(row);
    if (!pendingUpload) uploadDone += 1;
    if (isOverduePlaceholder(row)) overdueCount += 1;

    const sign = resolvePlaceholderSignProgress(
      row,
      staffList,
      roleOptions,
      documents,
    );
    if (studyPlaceholderRequiresSignature(row) && sign.document) {
      signTotal += sign.totalSigners || 0;
      signDone += sign.signedCount || 0;
    }

    const treeNode = placeholderToTreeNode(row);
    const subtitle = buildTaskSubtitle(row);
    const name = formatStudyPlaceholderTreeLabel(row);
    const isAdHoc = isAdHocStudyPlaceholder(row);

    if (pendingUpload) {
      const canUpload = studyPlaceholderCanUpload(row, userData, {
        staffList,
        roleOptions,
      });
      tasks.push({
        id: `upload-${row.StudyPlaceholderId}`,
        type: "upload",
        title: name,
        subtitle,
        status: isOverduePlaceholder(row) ? "Overdue" : "Upload pending",
        isMine: canUpload,
        canAct: canUpload,
        actionLabel: "Upload",
        isAdHoc,
        treeNode,
        placeholder: row,
      });
    } else if (
      hasPendingSignature(row, staffList, roleOptions, documents)
    ) {
      const canSign = isUserEligibleToSignPlaceholder(
        row,
        documents,
        userData,
        staffList,
        roleOptions,
      );
      const doc = sign.document;
      tasks.push({
        id: `sign-${row.StudyPlaceholderId}`,
        type: "sign",
        title: doc?.FileName || name,
        subtitle,
        status: "Signature pending",
        isMine: canSign,
        canAct: canSign,
        actionLabel: "Sign",
        isAdHoc,
        treeNode: documentToTreeNode(doc, row),
        placeholder: row,
        document: doc,
      });
    }
  });

  tasks.sort((a, b) => {
    if (a.canAct !== b.canAct) return a.canAct ? -1 : 1;
    if (a.status === "Overdue" && b.status !== "Overdue") return -1;
    if (b.status === "Overdue" && a.status !== "Overdue") return 1;
    return String(a.title).localeCompare(String(b.title), undefined, {
      sensitivity: "base",
    });
  });

  const myTasks = tasks.filter((t) => t.canAct);

  return {
    kpis: {
      uploadDone,
      uploadTotal,
      signDone,
      signTotal,
      overdueCount,
      myTasksCount: myTasks.length,
    },
    tasks: tasks,
    myTasks,
  };
}

function formatDueLabel(row) {
  const due = row?.DueDate || row?.dueDate;
  if (!due) return null;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function normalizeSiteIdRef(id) {
  return String(id || "")
    .replace(/[{}]/g, "")
    .trim()
    .toLowerCase();
}

export function resolveQueueSiteName(placeholder, siteLookup = null) {
  if (!placeholder) return "";

  const direct = String(placeholder.SiteName || placeholder.siteName || "").trim();
  if (direct) return direct;

  const siteId = normalizeSiteIdRef(
    placeholder.ResolvedSiteIdRef ||
      placeholder.resolvedSiteIdRef ||
      placeholder.SiteIdRef ||
      placeholder.siteIdRef,
  );

  if (siteId && siteLookup instanceof Map && siteLookup.has(siteId)) {
    return siteLookup.get(siteId) || "";
  }

  return "";
}

export function buildDashboardTaskQueue({
  studyBuckets = [],
  userData = null,
  roleOptions = [],
  siteLookup = null,
} = {}) {
  const tasks = [];

  studyBuckets.forEach(
    ({ studyId, studyName, studyNumber, studyColor, placeholders = [], documents = [], staffList = [] }) => {
      const snapshot = buildSiteOverviewSnapshot({
        placeholders,
        documents,
        userData,
        staffList,
        roleOptions,
      });

      snapshot.tasks.forEach((task) => {
        if (task.type !== "upload" && task.type !== "sign") return;
        const ph = task.placeholder || {};
        tasks.push({
          ...task,
          studyId,
          studyName,
          studyNumber: studyNumber || "",
          studyColor: studyColor || "",
          siteName: resolveQueueSiteName(ph, siteLookup),
          dueLabel: formatDueLabel(task.placeholder),
        });
      });
    },
  );

  tasks.sort((a, b) => {
    if (a.canAct !== b.canAct) return a.canAct ? -1 : 1;
    if (a.status === "Overdue" && b.status !== "Overdue") return -1;
    if (b.status === "Overdue" && a.status !== "Overdue") return 1;
    const studyCmp = String(a.studyName || "").localeCompare(String(b.studyName || ""), undefined, {
      sensitivity: "base",
    });
    if (studyCmp !== 0) return studyCmp;
    return String(a.title).localeCompare(String(b.title), undefined, { sensitivity: "base" });
  });

  const myTasks = tasks.filter((t) => t.canAct);
  const overdueMine = myTasks.filter((t) => t.status === "Overdue").length;

  return {
    tasks,
    myTasks,
    counts: {
      myTasks: myTasks.length,
      myUploads: myTasks.filter((t) => t.type === "upload").length,
      mySignatures: myTasks.filter((t) => t.type === "sign").length,
      allOpen: tasks.length,
      overdueMine,
      uploads: tasks.filter((t) => t.type === "upload").length,
      signatures: tasks.filter((t) => t.type === "sign").length,
    },
  };
}
